/**
 * Inventory Validation System - Robust Version
 * Prevents adding sold-out variants to the cart
 * Handles CSP restrictions and form conflicts
 */

(function() {
  'use strict';
  
  console.log('🔒 Inventory validation script loaded - robust version');
  
  // Configuration
  const CONFIG = {
    checkInterval: 1000, // Check every second
    maxRetries: 10,      // Maximum retry attempts
    debugMode: true      // Enable debug logging
  };
  
  // State tracking
  let isInitialized = false;
  let retryCount = 0;
  let checkInterval = null;
  let hasUserInteracted = false; // Track if user has selected a variant
  let originalButtonText = null; // Store original button text
  
  // Debug logging
  function debugLog(message, type = 'log') {
    if (CONFIG.debugMode) {
      const timestamp = new Date().toLocaleTimeString();
      console.log(`🔒 [${timestamp}] ${message}`);
    }
  }
  
  // Wait for elements with retry logic
  function waitForElements() {
    debugLog('🔍 Waiting for required elements...');
    
    const productForm = document.querySelector('.product-form, form[action*="/cart/add"]');
    const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
    
    if (productForm && addToCartButton) {
      // Store original button text IMMEDIATELY - this is critical!
      // Check for empty string explicitly to ensure we always have a valid fallback
      if (!originalButtonText && addToCartButton) {
        // Try multiple ways to get the button text
        let currentValue = '';
        if (addToCartButton.tagName === 'INPUT') {
          currentValue = addToCartButton.value || '';
        } else {
          currentValue = addToCartButton.textContent || '';
        }
        
        // Store the value (even if empty, we'll use fallback)
        originalButtonText = currentValue.trim() || 'Add to Cart';
        debugLog(`💾 Stored original button text: "${originalButtonText}"`);
        
        // CRITICAL: If button is empty, set it immediately to prevent blank button
        if (!currentValue.trim()) {
          debugLog('⚠️ Button text is empty, setting default immediately');
          if (addToCartButton.tagName === 'INPUT') {
            addToCartButton.value = originalButtonText;
          } else {
            addToCartButton.textContent = originalButtonText;
          }
          // Also retry after a delay in case Liquid renders it later
          setTimeout(() => {
            const retryValue = addToCartButton.tagName === 'INPUT' 
              ? addToCartButton.value 
              : addToCartButton.textContent;
            if (retryValue && retryValue.trim() && retryValue.trim() !== 'Add to Cart') {
              originalButtonText = retryValue.trim();
              debugLog(`💾 Updated original button text from Liquid: "${originalButtonText}"`);
            }
          }, 200);
        }
      }
      debugLog('✅ Found product form and add to cart button');
      initializeValidation();
    } else {
      retryCount++;
      if (retryCount < CONFIG.maxRetries) {
        debugLog(`⏳ Retry ${retryCount}/${CONFIG.maxRetries} - waiting for elements...`);
        setTimeout(waitForElements, CONFIG.checkInterval);
      } else {
        debugLog('❌ Max retries reached - elements not found');
        // Try alternative approach
        tryAlternativeInitialization();
      }
    }
  }
  
  // Alternative initialization for when elements aren't found
  function tryAlternativeInitialization() {
    debugLog('🔄 Trying alternative initialization...');
    
    // Look for any form that might be a product form
    const forms = document.querySelectorAll('form');
    let productForm = null;
    
    forms.forEach(form => {
      if (form.action && form.action.includes('/cart/add')) {
        productForm = form;
        debugLog('✅ Found product form via action attribute');
      }
    });
    
    if (productForm) {
      // Look for submit button in this form
      const submitButton = productForm.querySelector('input[type="submit"], button[type="submit"]');
      if (submitButton) {
        // Store original button text if not already stored
        if (!originalButtonText && submitButton) {
          originalButtonText = submitButton.value || submitButton.textContent || 'Add to Cart';
          debugLog(`💾 Stored original button text: "${originalButtonText}"`);
        }
        debugLog('✅ Found submit button via alternative method');
        initializeValidationWithElements(productForm, submitButton);
      }
    }
  }
  
  // Initialize validation system
  function initializeValidation() {
    if (isInitialized) {
      debugLog('⚠️ Already initialized, skipping...');
      return;
    }
    
    debugLog('🚀 Initializing inventory validation...');
    
    const productForm = document.querySelector('.product-form, form[action*="/cart/add"]');
    const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
    
    if (!productForm || !addToCartButton) {
      debugLog('❌ Required elements not found for initialization');
      return;
    }
    
    initializeValidationWithElements(productForm, addToCartButton);
  }
  
  // Initialize with specific elements
  function initializeValidationWithElements(productForm, addToCartButton) {
    debugLog('🔧 Setting up validation with found elements');
    
    // Store original button text if not already stored
    // Check for empty string explicitly to ensure we always have a valid fallback
    if (!originalButtonText && addToCartButton) {
      const currentValue = addToCartButton.tagName === 'INPUT' 
        ? addToCartButton.value 
        : addToCartButton.textContent;
      originalButtonText = (currentValue || '').trim() || 'Add to Cart';
      debugLog(`💾 Stored original button text: "${originalButtonText}"`);
      
      // CRITICAL: If button is empty, set it immediately
      if (!currentValue || !currentValue.trim()) {
        debugLog('⚠️ Button text is empty in initializeValidationWithElements, setting default');
        if (addToCartButton.tagName === 'INPUT') {
          addToCartButton.value = originalButtonText;
        } else {
          addToCartButton.textContent = originalButtonText;
        }
      }
    }
    
    // Store references
    window.inventoryValidationElements = {
      form: productForm,
      button: addToCartButton
    };
    
    // Add form submission validation (use capture to run first)
    try {
      productForm.addEventListener('submit', validateBeforeSubmit, true);
      debugLog('✅ Form submit validation added');
    } catch (error) {
      debugLog(`❌ Failed to add form submit validation: ${error.message}`);
    }
    
    // Add variant change validation
    setupVariantChangeValidation();
    
    // Sync dropdown with selected swatches on initialization
    setTimeout(syncDropdownWithSwatches, 300);
    
    // Initial validation - wait for Travel Case variants if needed
    waitForTravelCaseVariantsAndValidate();
    
    // Set up periodic validation
    setupPeriodicValidation();
    
    // Ensure button text is preserved immediately and after a short delay
    ensureButtonTextPreserved();
    
    // Immediate check: if a swatch is selected, enable the button
    const selectedOptions = getSelectedSwatchOptions();
    if (selectedOptions.length > 0) {
      debugLog(`🎨 Found ${selectedOptions.length} selected swatch(es) on init - ensuring button is enabled`);
      const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
      if (addToCartButton) {
        const currentValue = addToCartButton.tagName === 'INPUT' ? addToCartButton.value : addToCartButton.textContent;
        if (currentValue !== 'Out of Stock') {
          addToCartButton.disabled = false;
          addToCartButton.classList.remove('disabled');
          addToCartButton.style.opacity = '';
          addToCartButton.style.removeProperty('opacity');
          if (!currentValue || currentValue.trim() === '') {
            const buttonText = originalButtonText || 'Add to Cart';
            if (addToCartButton.tagName === 'INPUT') {
              addToCartButton.value = buttonText;
            } else {
              addToCartButton.textContent = buttonText;
            }
          }
          debugLog('✅ Button enabled on init due to selected swatch');
        }
      }
    }
    
    setTimeout(() => {
      ensureButtonTextPreserved();
    }, 500);
    
    isInitialized = true;
    debugLog('✅ Inventory validation initialization complete');
  }
  
  // Wait for Travel Case variants to load before initial validation
  function waitForTravelCaseVariantsAndValidate(attempts = 0) {
    const maxAttempts = 10;
    const delay = 200;
    
    // Check if this might be a Travel Case product (check page title or URL)
    const mightBeTravelCase = document.title.includes('Travel Case') || 
                               window.location.pathname.includes('travel-case') ||
                               window.location.pathname.includes('travel-cases');
    
    // CRITICAL: If a swatch is selected, enable button immediately (don't wait for variants)
    const selectedOptions = getSelectedSwatchOptions();
    if (selectedOptions.length > 0) {
      const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
      if (addToCartButton && addToCartButton.disabled) {
        const currentValue = addToCartButton.tagName === 'INPUT' ? addToCartButton.value : addToCartButton.textContent;
        if (currentValue !== 'Out of Stock') {
          debugLog(`🎨 Swatch selected during wait - enabling button immediately`);
          addToCartButton.disabled = false;
          addToCartButton.classList.remove('disabled');
          addToCartButton.style.opacity = '';
          addToCartButton.style.removeProperty('opacity');
          if (!currentValue || currentValue.trim() === '') {
            const buttonText = originalButtonText || 'Add to Cart';
            if (addToCartButton.tagName === 'INPUT') {
              addToCartButton.value = buttonText;
            } else {
              addToCartButton.textContent = buttonText;
            }
          }
        }
      }
    }
    
    if (mightBeTravelCase && (!window.travelCaseVariants || Object.keys(window.travelCaseVariants).length === 0)) {
      if (attempts < maxAttempts) {
        debugLog(`⏳ Waiting for Travel Case variants to load... (attempt ${attempts + 1}/${maxAttempts})`);
        setTimeout(() => waitForTravelCaseVariantsAndValidate(attempts + 1), delay);
        return;
      } else {
        debugLog(`⚠️ Travel Case variants not loaded after ${maxAttempts} attempts, proceeding with validation`);
        // Even if variants didn't load, ensure button text is preserved and enabled if swatch selected
        ensureButtonTextPreserved();
      }
    }
    
    // Proceed with validation
    setTimeout(validateCurrentVariant, 100);
  }
  
  // Ensure button text is preserved (helper function)
  function ensureButtonTextPreserved() {
    const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
    if (addToCartButton) {
      // Check if a swatch is selected
      const selectedOptions = getSelectedSwatchOptions();
      
      // If button text is empty or missing, restore it
      const currentValue = addToCartButton.tagName === 'INPUT' 
        ? addToCartButton.value 
        : addToCartButton.textContent;
      
      // If button is blank or empty, always set it to something
      if (!currentValue || currentValue.trim() === '') {
        const buttonText = originalButtonText || 'Add to Cart';
        if (addToCartButton.tagName === 'INPUT') {
          addToCartButton.value = buttonText;
        } else {
          addToCartButton.textContent = buttonText;
        }
        debugLog(`💾 Ensured button text preserved (was empty): "${buttonText}"`);
      }
      
      // If a swatch is selected, ensure button is enabled (unless explicitly out of stock)
      if (selectedOptions.length > 0 && currentValue !== 'Out of Stock') {
        // Always ensure enabled if swatch selected (override swatch-switch.js)
        if (addToCartButton.disabled) {
          debugLog(`💾 Swatch selected (${selectedOptions.length} options) but button disabled - enabling it`);
        }
        addToCartButton.disabled = false;
        addToCartButton.removeAttribute('disabled'); // Remove disabled attribute completely
        addToCartButton.classList.remove('disabled');
        addToCartButton.style.opacity = '';
        addToCartButton.style.removeProperty('opacity'); // Remove inline opacity style
        addToCartButton.title = '';
        // Ensure button text is set
        if (!currentValue || currentValue.trim() === '' || currentValue === 'Out of Stock') {
          const buttonText = originalButtonText || 'Add to Cart';
          if (addToCartButton.tagName === 'INPUT') {
            addToCartButton.value = buttonText;
          } else {
            addToCartButton.textContent = buttonText;
          }
        }
      }
      
      // Update originalButtonText if we got a good value and haven't stored it yet
      if (currentValue && currentValue.trim() && (!originalButtonText || originalButtonText === 'Add to Cart')) {
        // Only update if current value is not "Out of Stock" (that's a temporary state)
        if (currentValue.trim() !== 'Out of Stock') {
          originalButtonText = currentValue.trim();
          debugLog(`💾 Updated originalButtonText from button: "${originalButtonText}"`);
        }
      }
    }
  }
  
  // Set up periodic validation to catch any missed changes
  function setupPeriodicValidation() {
    if (checkInterval) {
      clearInterval(checkInterval);
    }
    
    checkInterval = setInterval(() => {
      if (isInitialized) {
        // First, ensure button stays enabled if swatch is selected (override swatch-switch.js)
        const selectedOptions = getSelectedSwatchOptions();
        if (selectedOptions.length > 0) {
          const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
          if (addToCartButton && addToCartButton.disabled) {
            const currentValue = addToCartButton.tagName === 'INPUT' ? addToCartButton.value : addToCartButton.textContent;
            if (currentValue !== 'Out of Stock') {
              debugLog('🔄 Periodic check: Swatch selected but button disabled - re-enabling');
              addToCartButton.disabled = false;
              addToCartButton.removeAttribute('disabled');
              addToCartButton.classList.remove('disabled');
              addToCartButton.style.opacity = '';
              addToCartButton.style.removeProperty('opacity');
              addToCartButton.title = '';
              if (!currentValue || currentValue.trim() === '') {
                const buttonText = originalButtonText || 'Add to Cart';
                if (addToCartButton.tagName === 'INPUT') {
                  addToCartButton.value = buttonText;
                } else {
                  addToCartButton.textContent = buttonText;
                }
              }
            }
          }
        }
        // Then run normal validation
        validateCurrentVariant();
      }
    }, CONFIG.checkInterval);
    
    debugLog('⏰ Periodic validation set up');
  }
  
  // Set up variant change validation
  function setupVariantChangeValidation() {
    debugLog('🎯 Setting up variant change validation...');
    
    // Listen for variant changes on swatches
    const swatchInputs = document.querySelectorAll('.swatch-container input[type="radio"], .swatch-wrapper input[type="radio"]');
    debugLog(`🔍 Found ${swatchInputs.length} swatch inputs`);
    
    swatchInputs.forEach(input => {
      try {
        input.addEventListener('change', function() {
          hasUserInteracted = true; // Mark that user has selected a variant
          debugLog(`🎨 Swatch changed: ${this.name} = ${this.value}`);
          setTimeout(validateCurrentVariant, 100);
        });
      } catch (error) {
        debugLog(`❌ Failed to add swatch listener: ${error.message}`);
      }
    });
    
    // Listen for variant changes on dropdown
    const variantSelect = document.querySelector('.product-select, select[name="id"]');
    if (variantSelect) {
      try {
        variantSelect.addEventListener('change', function() {
          hasUserInteracted = true; // Mark that user has selected a variant
          debugLog(`🎯 Variant select changed: ${this.value}`);
          setTimeout(validateCurrentVariant, 100);
        });
        debugLog('✅ Variant select listener added');
      } catch (error) {
        debugLog(`❌ Failed to add variant select listener: ${error.message}`);
      }
    }
    
    // Listen for custom variant change events
    try {
      document.addEventListener('variantChanged', function(e) {
        hasUserInteracted = true; // Mark that user has selected a variant
        debugLog(`🎭 Variant change event: ${e.detail.type} = ${e.detail.value}`);
        setTimeout(validateCurrentVariant, 100);
      });
      debugLog('✅ Custom variant change listener added');
    } catch (error) {
      debugLog(`❌ Failed to add custom variant change listener: ${error.message}`);
    }
  }
  
  // Validate current variant
  function validateCurrentVariant() {
    debugLog('🔍 Validating current variant...');
    
    // Check if a swatch is selected first
    const selectedOptions = getSelectedSwatchOptions();
    const currentVariant = getCurrentVariant();
    
    if (!currentVariant) {
      debugLog('⚠️ No variant found for validation');
      // If a swatch IS selected but variant not found, that's a problem - enable button anyway
      // If NO swatch is selected, leave it disabled (swatch-switch.js handles this)
      if (selectedOptions.length > 0) {
        debugLog('⚠️ Swatch selected but variant not found - enabling button anyway');
        const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
        if (addToCartButton) {
          // Restore button text and enable it
          const buttonText = originalButtonText || 'Add to Cart';
          if (addToCartButton.tagName === 'INPUT') {
            addToCartButton.value = buttonText;
          } else {
            addToCartButton.textContent = buttonText;
          }
          addToCartButton.disabled = false;
          addToCartButton.classList.remove('disabled');
          addToCartButton.title = '';
          debugLog(`💾 Enabled button when swatch selected but variant not found: "${buttonText}"`);
        }
      } else {
        // No swatch selected - ensure button text is preserved but leave disabled state to swatch-switch.js
        const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
        if (addToCartButton) {
          const currentValue = addToCartButton.tagName === 'INPUT' ? addToCartButton.value : addToCartButton.textContent;
          if (currentValue === 'Out of Stock' || !currentValue || currentValue.trim() === '') {
            const buttonText = originalButtonText || 'Add to Cart';
            if (addToCartButton.tagName === 'INPUT') {
              addToCartButton.value = buttonText;
            } else {
              addToCartButton.textContent = buttonText;
            }
            // Remove disabled class and title if it was set by disableAddToCart
            addToCartButton.classList.remove('disabled');
            addToCartButton.title = '';
            debugLog(`💾 Restored button text when no variant found: "${buttonText}"`);
          }
        }
      }
      return;
    }
    
    debugLog(`🔍 Validating variant: ${currentVariant.title} (ID: ${currentVariant.id})`);
    debugLog(`📊 Inventory data - Available: ${currentVariant.available}, Quantity: ${currentVariant.inventory_quantity}, Management: ${currentVariant.inventory_management}`);
    
    // Check if variant is unavailable
    // Only check quantity if inventory_management is explicitly set to 'shopify'
    // If inventory_management is null/undefined, Shopify doesn't track inventory, so quantity doesn't matter
    if (!currentVariant.available) {
      debugLog(`❌ Variant marked as unavailable`);
      disableAddToCart('This variant is currently out of stock');
    } else if (currentVariant.inventory_management === 'shopify' && 
               currentVariant.inventory_quantity !== null && 
               currentVariant.inventory_quantity !== undefined && 
               currentVariant.inventory_quantity <= 0) {
      // Only check quantity if Shopify is managing inventory AND quantity is explicitly 0 or less
      debugLog(`❌ Variant has 0 inventory quantity with Shopify management (${currentVariant.inventory_quantity})`);
      disableAddToCart('This variant is out of stock');
    } else {
      debugLog(`✅ Variant is available`);
      enableAddToCart();
    }
  }
  
  // Check if this is a Travel Case product page
  // NOTE: travelCaseVariants is for the SEPARATE Travel Cases product used for bundling
  // For the Travel Cases product page itself, we should use window.productVariants
  function isTravelCaseProduct() {
    // Check if this is the Travel Cases product page (not a pillow product with travel case bundling)
    const isTravelCasePage = document.title.includes('Travel Case') || 
                             document.title.includes('Travel Cases') ||
                             window.location.pathname.includes('travel-case') ||
                             window.location.pathname.includes('travel-cases');
    
    // Only use travelCaseVariants if this is NOT the Travel Cases product page itself
    // (travelCaseVariants is for bundling on other product pages)
    const useTravelCaseVariants = !isTravelCasePage && window.travelCaseVariants && Object.keys(window.travelCaseVariants).length > 0;
    
    debugLog(`🔍 Is Travel Case product page: ${isTravelCasePage}`);
    debugLog(`🔍 Use travelCaseVariants for inventory: ${useTravelCaseVariants}`);
    return useTravelCaseVariants;
  }
  
  // Get inventory data for Travel Case variant by matching option values
  function getTravelCaseInventoryByOptions(selectedOptions) {
    if (!window.travelCaseVariants || selectedOptions.length === 0) {
      debugLog(`⚠️ Cannot get Travel Case inventory: travelCaseVariants=${!!window.travelCaseVariants}, selectedOptions.length=${selectedOptions.length}`);
      return null;
    }
    
    debugLog(`🔍 Looking for Travel Case variant matching options:`, selectedOptions);
    debugLog(`🔍 Available Travel Case variants:`, Object.keys(window.travelCaseVariants));
    
    // Find matching variant in travelCaseVariants by option values
    for (const variantId in window.travelCaseVariants) {
      const variant = window.travelCaseVariants[variantId];
      debugLog(`🔍 Checking Travel Case variant ${variantId}: option1=${variant.option1}, option2=${variant.option2}, option3=${variant.option3}`);
      if (variantMatchesOptions(variant, selectedOptions)) {
        debugLog(`✅ Found matching Travel Case variant for inventory: ${variant.title} (Available: ${variant.available}, Qty: ${variant.inventory_quantity})`);
        return variant;
      }
    }
    
    debugLog(`⚠️ No matching Travel Case variant found for options:`, selectedOptions);
    return null;
  }
  
  // Get current variant
  function getCurrentVariant() {
    // First, try to get from swatch selection (prioritize for single-option products like Travel Cases)
    const selectedOptions = getSelectedSwatchOptions();
    debugLog(`🔍 Selected swatch options:`, selectedOptions);
    
    // For Travel Case products, get inventory from travelCaseVariants
    if (isTravelCaseProduct() && selectedOptions.length > 0) {
      const travelCaseVariant = getTravelCaseInventoryByOptions(selectedOptions);
      if (travelCaseVariant) {
        // Get the variant from productVariants for other data, but use inventory from travelCaseVariants
        if (window.productVariants) {
          for (const variantId in window.productVariants) {
            const productVariant = window.productVariants[variantId];
            if (variantMatchesOptions(productVariant, selectedOptions)) {
              // Merge: use productVariant data but override with travelCaseVariant inventory
              const mergedVariant = {
                ...productVariant,
                available: travelCaseVariant.available,
                inventory_quantity: travelCaseVariant.inventory_quantity,
                inventory_management: travelCaseVariant.inventory_management
              };
              debugLog(`✅ Found matching variant with Travel Case inventory: ${mergedVariant.title}`);
              debugLog(`📊 Using inventory from Travel Case product: Available=${mergedVariant.available}, Qty=${mergedVariant.inventory_quantity}`);
              return mergedVariant;
            }
          }
        }
      }
    }
    
    // Standard flow for non-Travel Case products or fallback
    if (selectedOptions.length > 0 && window.productVariants) {
      // Find matching variant based on selected options
      for (const variantId in window.productVariants) {
        const variant = window.productVariants[variantId];
        if (variantMatchesOptions(variant, selectedOptions)) {
          debugLog(`✅ Found matching variant from swatches: ${variant.title}`);
          return variant;
        }
      }
    }
    
    // Fallback: try to get variant from the product select dropdown
    const productSelect = document.querySelector('.product-select, select[name="id"]');
    if (productSelect && productSelect.value) {
      const variantId = productSelect.value;
      debugLog(`🔍 Product select variant ID: ${variantId}`);
      
      // For Travel Case products (when bundling), try to get inventory from travelCaseVariants
      // BUT: If this IS the Travel Cases product page, use window.productVariants directly
      if (isTravelCaseProduct() && window.productVariants && window.productVariants[variantId]) {
        const productVariant = window.productVariants[variantId];
        // Try to find matching variant in travelCaseVariants by option values
        const travelCaseVariant = getTravelCaseInventoryByOptions([
          productVariant.option1,
          productVariant.option2,
          productVariant.option3
        ].filter(Boolean));
        
        if (travelCaseVariant) {
          // Merge inventory data
          const mergedVariant = {
            ...productVariant,
            available: travelCaseVariant.available,
            inventory_quantity: travelCaseVariant.inventory_quantity,
            inventory_management: travelCaseVariant.inventory_management
          };
          debugLog(`✅ Found matching variant from dropdown with Travel Case inventory: ${mergedVariant.title}`);
          debugLog(`📊 Using inventory from Travel Case product: Available=${mergedVariant.available}, Qty=${mergedVariant.inventory_quantity}`);
          return mergedVariant;
        }
      }
      
      // For Travel Cases product page itself, use window.productVariants directly
      
      // Check if the selected variant is actually available (not disabled)
      const selectedOption = productSelect.options[productSelect.selectedIndex];
      if (selectedOption && !selectedOption.disabled && window.productVariants && window.productVariants[variantId]) {
        debugLog(`✅ Found matching variant from dropdown: ${window.productVariants[variantId].title}`);
        return window.productVariants[variantId];
      } else if (selectedOption && selectedOption.disabled) {
        debugLog(`⚠️ Selected dropdown option is disabled, ignoring dropdown value`);
      }
    }
    
    debugLog('❌ No matching variant found');
    return null;
  }
  
  // Sync dropdown with selected swatches
  function syncDropdownWithSwatches() {
    const selectedOptions = getSelectedSwatchOptions();
    if (selectedOptions.length === 0) {
      debugLog('⚠️ No swatches selected, skipping dropdown sync');
      return;
    }
    
    debugLog(`🔄 Syncing dropdown with swatches:`, selectedOptions);
    
    // Find matching variant
    if (window.productVariants) {
      for (const variantId in window.productVariants) {
        const variant = window.productVariants[variantId];
        if (variantMatchesOptions(variant, selectedOptions)) {
          // Update dropdown
          const productSelect = document.querySelector('.product-select, select[name="id"]');
          if (productSelect && productSelect.value !== variantId) {
            debugLog(`🔄 Updating dropdown from ${productSelect.value} to ${variantId} (${variant.title})`);
            // Just update the value - don't dispatch change event to avoid triggering user interaction flag
            productSelect.value = variantId;
          }
          return;
        }
      }
    }
    
    debugLog('⚠️ Could not find matching variant for selected swatches');
  }
  
  // Get selected swatch options
  function getSelectedSwatchOptions() {
    const selectedOptions = window.getSelectedProductOptions();
    debugLog(`🎨 Selected swatch options: [${selectedOptions.join(', ')}]`);
    return selectedOptions;
  }
  
  function variantMatchesOptions(variant, selectedOptions) {
    return window.variantMatchesProductOptions(variant, selectedOptions);
  }
  
  // Disable add to cart
  function disableAddToCart(reason) {
    debugLog(`🚫 Disabling add to cart: ${reason}`);
    
    // Try multiple selectors for the add to cart button
    const buttonSelectors = [
      '.add-to-cart-button',
      '.product-form-submit-wrap input[type="submit"]',
      'form[action*="/cart/add"] input[type="submit"]',
      'form[action*="/cart/add"] button[type="submit"]'
    ];
    
    let addToCartButton = null;
    for (const selector of buttonSelectors) {
      addToCartButton = document.querySelector(selector);
      if (addToCartButton) break;
    }
    
    if (addToCartButton) {
      // Preserve original button text before changing it (if not already stored)
      if (!originalButtonText || originalButtonText.trim() === '') {
        const currentValue = addToCartButton.value || addToCartButton.textContent || '';
        const trimmedValue = currentValue.trim();
        if (trimmedValue && trimmedValue !== 'Out of Stock') {
          originalButtonText = trimmedValue;
          debugLog(`💾 Preserved button text before disabling: "${originalButtonText}"`);
        }
      }
      
      addToCartButton.disabled = true;
      addToCartButton.classList.add('disabled');
      addToCartButton.value = 'Out of Stock';
      addToCartButton.title = reason;
      debugLog('✅ Add to cart button disabled');
    } else {
      debugLog('❌ Add to cart button not found for disabling');
    }
    
    // Also disable payment buttons
    const paymentButtons = document.querySelectorAll('[data-shopify="payment-button"] button, .shopify-payment-button button');
    paymentButtons.forEach(button => {
      button.disabled = true;
      button.classList.add('disabled');
    });
    
    // Show out of stock message
    showOutOfStockMessage(reason);
  }
  
  // Enable add to cart
  function enableAddToCart() {
    debugLog('✅ Enabling add to cart');
    
    // Try multiple selectors for the add to cart button
    const buttonSelectors = [
      '.add-to-cart-button',
      '.product-form-submit-wrap input[type="submit"]',
      'form[action*="/cart/add"] input[type="submit"]',
      'form[action*="/cart/add"] button[type="submit"]'
    ];
    
    let addToCartButton = null;
    for (const selector of buttonSelectors) {
      addToCartButton = document.querySelector(selector);
      if (addToCartButton) break;
    }
    
    if (addToCartButton) {
      addToCartButton.disabled = false;
      addToCartButton.classList.remove('disabled');
      
      // Restore original button text or use default
      // If originalButtonText is empty/null/undefined, try to get current value, otherwise use fallback
      let buttonText = originalButtonText;
      if (!buttonText || buttonText.trim() === '') {
        // Try to get current button value first (in case it was set by Liquid)
        const currentValue = addToCartButton.value || addToCartButton.textContent || '';
        buttonText = currentValue.trim() || 'Add to Cart';
        // Update stored value for next time
        if (buttonText !== 'Add to Cart') {
          originalButtonText = buttonText;
        }
      }
      
      if (addToCartButton.tagName === 'INPUT') {
        addToCartButton.value = buttonText;
      } else {
        addToCartButton.textContent = buttonText;
      }
      addToCartButton.title = '';
      debugLog(`✅ Add to cart button enabled with text: "${buttonText}"`);
    }
    
    // Also enable payment buttons
    const paymentButtons = document.querySelectorAll('[data-shopify="payment-button"] button, .shopify-payment-button button');
    paymentButtons.forEach(button => {
      button.disabled = false;
      button.classList.remove('disabled');
    });
    
    // Hide out of stock message
    hideOutOfStockMessage();
  }
  
  // Show out of stock message
  function showOutOfStockMessage(reason) {
    // Only show message if user has interacted (selected a variant)
    // Don't show on initial page load
    if (!hasUserInteracted) {
      debugLog('⏸️ Skipping out of stock message - no user interaction yet');
      return;
    }
    
    // Remove existing message
    hideOutOfStockMessage();
    
    try {
      // Create out of stock message
      const messageDiv = document.createElement('div');
      messageDiv.className = 'out-of-stock-message';
      messageDiv.innerHTML = `
        <div style="background: #fff3cd; border: 1px solid #ffeaa7; color: #856404; padding: 12px; border-radius: 4px; margin: 10px 0; text-align: center;">
          <strong>⚠️ Out of Stock:</strong> ${reason}
        </div>
      `;
      
      // Try to insert the message
      const addToCartButton = document.querySelector('.add-to-cart-button, .product-form-submit-wrap input[type="submit"]');
      if (addToCartButton && addToCartButton.parentNode) {
        addToCartButton.parentNode.insertBefore(messageDiv, addToCartButton);
        debugLog('✅ Out of stock message displayed');
      }
    } catch (error) {
      debugLog(`❌ Failed to show out of stock message: ${error.message}`);
    }
  }
  
  // Hide out of stock message
  function hideOutOfStockMessage() {
    const existingMessage = document.querySelector('.out-of-stock-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }
  
  // Validate before form submission
  function validateBeforeSubmit(e) {
    debugLog('🔒 Form submission validation...');
    
    const currentVariant = getCurrentVariant();
    
    if (!currentVariant) {
      e.preventDefault();
      e.stopImmediatePropagation();
      alert('Please select a valid variant');
      return false;
    }
    
    if (!currentVariant.available) {
      e.preventDefault();
      e.stopImmediatePropagation();
      alert('This variant is currently out of stock and cannot be added to your cart.');
      return false;
    }
    
    if (currentVariant.inventory_quantity <= 0 && currentVariant.inventory_management === 'shopify') {
      e.preventDefault();
      e.stopImmediatePropagation();
      alert('This variant is out of stock and cannot be added to your cart.');
      return false;
    }
    
    // Check if quantity exceeds available inventory
    const quantityInput = document.querySelector('input[name="quantity"]');
    if (quantityInput && currentVariant.inventory_management === 'shopify') {
      const requestedQuantity = parseInt(quantityInput.value) || 1;
      if (requestedQuantity > currentVariant.inventory_quantity) {
        e.preventDefault();
        e.stopImmediatePropagation();
        alert(`Only ${currentVariant.inventory_quantity} units available. Please reduce quantity.`);
        return false;
      }
    }
    
    debugLog('✅ Inventory validation passed - allowing form submission');
    return true;
  }
  
  // Global function for external calls
  window.validateVariant = function(variantId) {
    if (window.productVariants && window.productVariants[variantId]) {
      const variant = window.productVariants[variantId];
      if (!variant.available) {
        disableAddToCart('This variant is currently out of stock');
        return false;
      } else if (variant.inventory_quantity <= 0 && variant.inventory_management === 'shopify') {
        disableAddToCart('This variant is out of stock');
        return false;
      } else {
        enableAddToCart();
        return true;
      }
    }
    return false;
  };
  
  // Export the InventoryValidator for global access
  window.InventoryValidator = {
    validateCurrentVariant: validateCurrentVariant,
    validateVariant: window.validateVariant,
    disableAddToCart: disableAddToCart,
    enableAddToCart: enableAddToCart,
    isInitialized: () => isInitialized
  };
  
  // Start the validation system
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForElements);
  } else {
    waitForElements();
  }
  
  debugLog('🚀 Inventory validation script initialization complete');
})();

